import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { FlipVertical, Edit, Trash, Package } from 'lucide-react';
import { useRegionStore } from '@/stores/regionStore';
import { Region, RegionWithCounts } from '@/features/regions/types';

interface RegionListProps {
  regions: Region[] | RegionWithCounts[];
  onSelectRegion?: (region: Region) => void;
  onDeleteRegion?: (region: Region) => void;
  onEditRegion?: (region: Region) => void;
  onViewVersions?: (region: Region) => void;
}

export function RegionList({
  regions = [],
  onSelectRegion,
  onDeleteRegion,
  onEditRegion,
  onViewVersions,
}: RegionListProps) {
  const { setSelectedRegion, openEditModal } = useRegionStore();

  // Ensure regions is always an array
  const safeRegions = Array.isArray(regions) ? regions : [];

  // Custom action handlers
  const handleSelect = (region: Region) => {
    if (!region) return;
    
    setSelectedRegion(region);
    if (onSelectRegion) onSelectRegion(region);
  };

  const handleEdit = (e: React.MouseEvent, region: Region) => {
    if (!region) return;
    
    e.stopPropagation();
    if (onEditRegion) {
      onEditRegion(region);
    } else {
      openEditModal(region);
    }
  };

  const handleDelete = (e: React.MouseEvent, region: Region) => {
    if (!region) return;
    
    e.stopPropagation();
    if (onDeleteRegion) onDeleteRegion(region);
  };

  const handleViewVersions = (e: React.MouseEvent, region: Region) => {
    if (!region) return;
    
    e.stopPropagation();
    if (onViewVersions) onViewVersions(region);
  };

  // Check if we have regions with counts
  const hasVersionCounts = safeRegions.length > 0 && 'versions' in (safeRegions[0] as any)?._count;

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Zip Code</TableHead>
            {hasVersionCounts && <TableHead>Versions</TableHead>}
            <TableHead className="w-[100px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {safeRegions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={hasVersionCounts ? 4 : 3} className="text-center">
                No regions found
              </TableCell>
            </TableRow>
          ) : (
            safeRegions.map((region) => (
              <TableRow
                key={region.id || `region-${Math.random()}`}
                onClick={() => handleSelect(region)}
                className="cursor-pointer hover:bg-muted/50"
              >
                <TableCell className="font-medium">{region.name || 'Unnamed Region'}</TableCell>
                <TableCell>
                  {region.zip_code ? region.zip_code : (
                    <span className="text-muted-foreground italic">None</span>
                  )}
                </TableCell>
                {hasVersionCounts && (
                  <TableCell>
                    {(region as RegionWithCounts)._count?.versions || 0}
                  </TableCell>
                )}
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <FlipVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {onViewVersions && (
                        <DropdownMenuItem onClick={(e: any) => handleViewVersions(e, region)}>
                          <Package className="mr-2 h-4 w-4" />
                          <span>View Versions</span>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={(e: any) => handleEdit(e, region)}>
                        <Edit className="mr-2 h-4 w-4" />
                        <span>Edit</span>
                      </DropdownMenuItem>
                      {onDeleteRegion && (
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={(e: any) => handleDelete(e, region)}
                        >
                          <Trash className="mr-2 h-4 w-4" />
                          <span>Delete</span>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
